import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

export default function NewsPage() {
  const { user } = useContext(AuthContext);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    content: '',
    news_type: 'other'
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get('/news');
      setNews(response.data);
    } catch (error) {
      console.error('Failed to fetch news', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post('/news', formData);
      toast.success('Novità pubblicata!');
      setDialogOpen(false);
      setFormData({ content: '', news_type: 'other' });
      fetchNews();
    } catch (error) {
      toast.error('Errore nella pubblicazione');
    }
  };

  const handleDelete = async (newsId) => {
    try {
      await axios.delete(`/news/${newsId}`);
      toast.success('Novità eliminata');
      fetchNews();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getNewsTypeLabel = (type) => {
    const types = {
      new_role: 'Nuovo Incarico',
      city_change: 'Cambio Città',
      other: 'Altro'
    };
    return types[type] || type;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="news-page">
      <Navbar />

      <div className="container mx-auto px-6 pt-24 pb-12 max-w-4xl">
        <div className="flex items-center justify-between mb-12">
          <h1 className="text-4xl font-bold" data-testid="page-title">Novità Personali</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-black text-white hover:bg-gray-800" data-testid="create-news-button">
                + Condividi Novità
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="news-dialog">
              <DialogHeader>
                <DialogTitle>Condividi una Novità</DialogTitle>
                <DialogDescription>Informa i membri su cambiamenti professionali o personali</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="news_type">Tipo di Novità</Label>
                  <Select
                    value={formData.news_type}
                    onValueChange={(value) => setFormData({...formData, news_type: value})}
                  >
                    <SelectTrigger data-testid="news-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_role">Nuovo Incarico</SelectItem>
                      <SelectItem value="city_change">Cambio Città</SelectItem>
                      <SelectItem value="other">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="content">Messaggio</Label>
                  <Textarea
                    id="content"
                    data-testid="news-content-input"
                    required
                    rows={4}
                    placeholder="Condividi la tua novità..."
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                  />
                </div>
                <Button type="submit" className="bg-black text-white hover:bg-gray-800 w-full" data-testid="submit-news-button">
                  Pubblica
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : news.length === 0 ? (
          <div className="text-center py-12 text-gray-600" data-testid="no-news-message">
            Nessuna novità condivisa.
          </div>
        ) : (
          <div className="space-y-4">
            {news.map((item) => (
              <Card key={item.id} className="border border-gray-200" data-testid="news-card">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{item.user_name}</CardTitle>
                      <CardDescription>
                        {getNewsTypeLabel(item.news_type)} • {new Date(item.created_at).toLocaleDateString('it-IT')}
                      </CardDescription>
                    </div>
                    {item.user_id === user?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                        data-testid="delete-news-button"
                      >
                        Elimina
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{item.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
